// src/prototype/artifacts/renderers/blockRenderers/index.tsx
// ============================================================
// Individual block renderers. Each handles one ArtifactBlockType.
// Used by ArtifactSectionRenderer to render each block in order.
// ============================================================

import React from 'react';
import { DvaArtifactBlock } from '../../types/artifactTypes';
import { MarkdownProse } from '../../../components/MarkdownProse';
import { formatPlainExecutiveText } from '../../../utils/executiveCopy';

function asText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (content == null) return '';
  return String(content);
}

// ── Paragraph ─────────────────────────────────────────────────

export const ParagraphBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => (
  <MarkdownProse>{asText(block.content)}</MarkdownProse>
);

// ── Callout ───────────────────────────────────────────────────

export const CalloutBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => (
  <div className="bg-[#18365E]/[0.05] border-l-4 border-[#18365E] rounded-r-lg p-3.5">
    {block.label && (
      <p className="text-[9px] font-bold text-[#18365E] uppercase tracking-widest mb-1.5">
        {formatPlainExecutiveText(block.label)}
      </p>
    )}
    <MarkdownProse tone="callout">{asText(block.content)}</MarkdownProse>
  </div>
);

// ── Stat Group ────────────────────────────────────────────────

interface StatItem { label: string; value: string; }

export const StatGroupBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => {
  const stats: StatItem[] = Array.isArray(block.content) ? block.content : [];
  const colClass = stats.length >= 4 ? 'grid-cols-2 sm:grid-cols-4' : `grid-cols-${stats.length}`;
  return (
    <div>
      {block.label && (
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">
          {formatPlainExecutiveText(block.label)}
        </p>
      )}
      <div className={`grid ${colClass} gap-3`}>
        {stats.map((stat, i) => (
          <div key={i} className="bg-[#F5F7FA] rounded-lg p-3 text-center">
            <p className="text-[9px] text-[#717182] uppercase tracking-wide mb-1">
              {formatPlainExecutiveText(stat.label)}
            </p>
            <p className="text-base font-bold text-[#18365E]">
              {formatPlainExecutiveText(stat.value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Bullet List ───────────────────────────────────────────────

export const BulletListBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => {
  const items: string[] = Array.isArray(block.content) ? block.content.map(asText) : [];
  return (
    <div>
      {block.label && (
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">
          {formatPlainExecutiveText(block.label)}
        </p>
      )}
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-[#374151]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6E4C] shrink-0 mt-2" />
            <MarkdownProse className="leading-relaxed min-w-0 flex-1">{item}</MarkdownProse>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── Table ─────────────────────────────────────────────────────

interface TableContent { headers: string[]; rows: string[][]; }

export const TableBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => {
  const { headers = [], rows = [] }: TableContent = block.content ?? {};
  return (
    <div>
      {block.label && (
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">
          {formatPlainExecutiveText(block.label)}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-[#e5e7eb]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F7FA]">
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2.5 text-left text-[10px] font-bold text-[#18365E] uppercase tracking-wide border-b border-[#e5e7eb]">
                  {formatPlainExecutiveText(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F5F7FA]/50'}>
                {row.map((cell, ci) => {
                  const text = formatPlainExecutiveText(cell);
                  return (
                    <td key={ci} className="px-3 py-2.5 text-[#374151] text-xs border-b border-[#e5e7eb] last:border-b-0">
                      {ci === 0 ? <span className="font-semibold text-[#18365E]">{text}</span> : text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Timeline Phase ────────────────────────────────────────────

interface TimelinePhaseContent { title: string; description: string; duration?: string; }

export const TimelinePhaseBlock: React.FC<{ block: DvaArtifactBlock; phaseNumber?: number; isLast?: boolean }> = ({
  block, phaseNumber = 1, isLast = false,
}) => {
  const { title, description, duration }: TimelinePhaseContent = block.content ?? {};
  const phaseLabel = formatPlainExecutiveText(block.label ?? `Phase ${phaseNumber}`);
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-6 h-6 rounded-full bg-[#18365E] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
          {phaseNumber}
        </div>
        {!isLast && <div className="w-px flex-1 bg-[#e5e7eb] my-1" style={{ minHeight: 20 }} />}
      </div>
      <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-3'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[#18365E] leading-tight">
              {phaseLabel} — {formatPlainExecutiveText(title)}
            </p>
            <MarkdownProse className="text-[10px] text-[#717182] mt-0.5 leading-snug">
              {description}
            </MarkdownProse>
          </div>
          {duration && (
            <span className="shrink-0 text-[9px] font-semibold text-[#717182] bg-[#F5F7FA] border border-[#e5e7eb] px-2 py-0.5 rounded-full whitespace-nowrap">
              {formatPlainExecutiveText(duration)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Risk Item ─────────────────────────────────────────────────

interface RiskContent { risk: string; mitigation: string; }

export const RiskItemBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => {
  const { risk, mitigation }: RiskContent = block.content ?? {};
  return (
    <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
      {block.label && (
        <p className="text-[9px] font-bold text-rose-700 uppercase tracking-widest mb-2">
          {formatPlainExecutiveText(block.label)}
        </p>
      )}
      <div className="space-y-2">
        <div>
          <p className="text-[10px] font-semibold text-[#374151] mb-0.5">Risk</p>
          <MarkdownProse className="text-xs text-[#374151]">{risk}</MarkdownProse>
        </div>
        {mitigation && (
          <div>
            <p className="text-[10px] font-semibold text-emerald-700 mb-0.5">Mitigation</p>
            <MarkdownProse className="text-xs text-[#374151]">{mitigation}</MarkdownProse>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Assumption Item ───────────────────────────────────────────

interface AssumptionContent { label: string; value?: string; note?: string; category?: string; }

const CATEGORY_COLORS: Record<string, string> = {
  financial:    'bg-blue-50 text-blue-700 border-blue-100',
  operational:  'bg-emerald-50 text-emerald-700 border-emerald-100',
  technical:    'bg-purple-50 text-purple-700 border-purple-100',
  data:         'bg-amber-50 text-amber-700 border-amber-100',
};

export const AssumptionItemBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => {
  const { label, value, note, category }: AssumptionContent = block.content ?? {};
  const colorClass = category ? (CATEGORY_COLORS[category] ?? 'bg-[#F5F7FA] text-[#717182] border-[#e5e7eb]') : 'bg-[#F5F7FA] text-[#717182] border-[#e5e7eb]';
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#e5e7eb] last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[#18365E]">{formatPlainExecutiveText(label)}</p>
        {note && <p className="text-[10px] text-[#717182] mt-0.5">{formatPlainExecutiveText(note)}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0 min-w-[11rem] justify-end">
        {category && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border capitalize w-[4.75rem] text-center ${colorClass}`}>
            {formatPlainExecutiveText(category)}
          </span>
        )}
        {value && (
          <span className="text-xs font-bold text-[#18365E] bg-[#F5F7FA] px-2 py-0.5 rounded border border-[#e5e7eb] whitespace-nowrap min-w-[6.5rem] text-right tabular-nums">
            {formatPlainExecutiveText(value)}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Tag Row ───────────────────────────────────────────────────

interface TagItem { label: string; value: string; }

export const TagRowBlock: React.FC<{ block: DvaArtifactBlock }> = ({ block }) => {
  const tags: TagItem[] = Array.isArray(block.content) ? block.content : [];
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag, i) => (
        <div key={i} className="flex items-center gap-1.5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-lg px-2.5 py-1.5">
          <span className="text-[9px] text-[#717182] font-medium uppercase tracking-wide">
            {formatPlainExecutiveText(tag.label)}
          </span>
          <span className="text-[#d1d5db]">·</span>
          <span className="text-xs font-semibold text-[#18365E]">
            {formatPlainExecutiveText(tag.value)}
          </span>
        </div>
      ))}
    </div>
  );
};